import { isSingleProperty } from '@innovyze/lib_asset_schema';
import {
	assetSchemaSelectorByType,
	getAssetSchemaAction,
} from '@innovyze/lib_asset_schema_store';
import { useIsFeatureEnabled } from '@innovyze/stylovyze';
import { selectAllRehabRuleChoiceListFiles } from '@Selectors/index';
import { AssetTypeId, SystemTypeId } from '@Types/assetSchema.types';
import { Rules } from '@Types/rehab.types';
import { RECORD_TYPE } from '@Utils/choiceListMapping';
import { getFieldSystemUnitType, unitMapping } from '@Utils/units/units.utils';
import _ from 'lodash';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type GetMeasurementUnit = (field: string, recordType: string) => string;

export const useMeasurementUnit = ({
	initialRuleValues,
	systemType,
	assetType,
	inspectionStandard,
	configValue,
}: {
	initialRuleValues: Rules;
	systemType: SystemTypeId;
	assetType: AssetTypeId;
	inspectionStandard?: string;
	configValue: string;
}): GetMeasurementUnit => {
	const recordTypeChoiceListFiles = selectAllRehabRuleChoiceListFiles();
	const shouldShowCustomTableUnits = useIsFeatureEnabled(
		'info-360-asset-rehab-custom-table-units',
	);

	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(
			getAssetSchemaAction({
				systemType: systemType.id,
				assetType: assetType.id,
			}),
		);
	}, [dispatch, systemType, assetType]);

	const schema = useSelector(state =>
		assetSchemaSelectorByType(state, assetType.id),
	);

	return useCallback(
		(newField: string, recordType: string): string => {
			if (recordType === RECORD_TYPE.USER_DEFINED_FIELDS) {
				if (!shouldShowCustomTableUnits) {
					return '';
				}

				const customTableName = configValue || initialRuleValues.config;

				const localUnit = recordTypeChoiceListFiles.udtRecordType
					.find(record => record.tableName === customTableName)
					?.fields.find(
						(field: { key: string }) => field.key === newField,
					)?.unit;

				return localUnit || initialRuleValues.unit || '';
			} else if (recordType === RECORD_TYPE.TASK) {
				if (!shouldShowCustomTableUnits) {
					return '';
				}

				const taskTypeId = configValue || initialRuleValues.config;

				const localUnit = recordTypeChoiceListFiles.tasksRecordType
					.find(record => record.uploadId === taskTypeId)
					?.fields.find(
						(field: { key: string }) => field.key === newField,
					)?.unit;

				return localUnit || initialRuleValues.unit || '';
			} else if (recordType === RECORD_TYPE.EVENT) {
				if (!shouldShowCustomTableUnits) {
					return '';
				}

				const eventId = configValue || initialRuleValues.config;

				const localUnit = recordTypeChoiceListFiles.eventsRecordType
					.find(record => record.uploadId === eventId)
					?.fields.find(
						(field: { key: string }) => field.key === newField,
					)?.unit;

				return localUnit || initialRuleValues.unit || '';
			} else if (recordType === RECORD_TYPE.ASSET_ATTRIBUTES) {
				if (!shouldShowCustomTableUnits) {
					return '';
				}

				const propertyKey = _.camelCase(newField);
				const field = schema?.getFlatProperty(propertyKey);

				if (field && isSingleProperty(field)) {
					return field?.units || '';
				}

				return '';
			} else if (recordType === RECORD_TYPE.ACTION) {
				// This is a special case for the action record type
				// Action is not part of the schema, so we need to return the unit for the cost field manually
				if (newField === 'COST') {
					return '$';
				}
				return '';
			}

			const fieldSystemUnitType = getFieldSystemUnitType(
				recordType,
				newField ?? '',
				inspectionStandard,
			);

			const fieldUnit = unitMapping(fieldSystemUnitType);

			return fieldUnit;
		},
		[initialRuleValues, inspectionStandard, configValue],
	);
};
